import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Tab, Tabs } from '@mui/material';

import PageContainer from '../../components/layout/PageContainer';
import { FORTA_BLUE } from '../../utils/colors';
import InvoiceBuilder from '../../components/layout/billings/invoices/InvoiceBuilder';
import InvoicePreview from '../../components/layout/billings/invoices/InvoicePreview';
import { TabPanel, a11yProps } from './common';
import {
  claimsActions,
  claimsInvoiceActions,
  clientActions,
  providerAssignmentActions,
} from '../../store';
import { FProgressBar } from '../../components/base';
import { filterAndTransformInvoiceTableData } from '../../components/layout/billings/invoices/utils';
import { dueDaysOptions } from '../../components/layout/billings/invoices/config';
import { ClinicalRelationships } from '../../constants';
import { ClinicalStatus } from '../../admin/constants/general';

import type { IInvoiceHistoryTableRowData } from '../../components/layout/billings/invoices/InvoiceHistoryTableRow';
import type { IOption } from '../../utils/types';
import type { RootState } from '../../store';
import type { SyntheticEvent } from 'react';

function BuildInvoice() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [tab, setTab] = useState<number>(0);
  const {
    invoicePreview,
    loading: invoiceDataLoading,
    selectedCleintToBuildInvoice,
  } = useSelector((state: RootState) => state.claimsInvoice);
  const { claimInvoiceHistoryList } = useSelector((state: RootState) => state.claims);
  const { loading: claimDataLoading } = useSelector((state: RootState) => state.claims);
  const { loading: clientsDataLoading } = useSelector((state: RootState) => state.clients);

  const [selectedClient, setSelectedClient] = useState<IOption | null>(
    selectedCleintToBuildInvoice ? selectedCleintToBuildInvoice : null,
  );
  const [discount, setDiscount] = useState<string | null>(null);
  const [selectedPayee, setSelectedPayee] = useState<number | null>(null);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [dueInDays, setDueInDays] = useState<number | null>(dueDaysOptions[0].value);
  const [transformedTableData, setTransformedTableData] = useState<IInvoiceHistoryTableRowData[]>(
    [],
  );

  useEffect(() => {
    dispatch(
      clientActions.getList({
        getAccessTokenSilently,
        clinical_status: [
          ClinicalStatus.CLINICAL,
          ClinicalStatus.OFFBOARDED,
          ClinicalStatus.ON_HOLD,
        ],
      }),
    );
    return () => {
      dispatch(claimsActions.clearState());
    };
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    const params = {
      client_id: selectedClient.id,
      invoice_history_attributes_only: true,
    };
    dispatch(
      claimsInvoiceActions.updateState({
        key: 'selectedCleintToBuildInvoice',
        data: selectedClient,
      }),
    );
    dispatch(claimsInvoiceActions.clearState());
    dispatch(claimsActions.getList({ getAccessTokenSilently, params }));
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        client_id: selectedClient.id,
        clinical_relationship: [
          ClinicalRelationships.Prbt,
          ClinicalRelationships.PracticingGuardian,
          ClinicalRelationships.LimitedAccessGuardian,
        ],
      }),
    );
    setIsSelectedAll(false);
    setSelectedPayee(null);
    setDueInDays(dueDaysOptions[0].value);
    setTransformedTableData([]);
  }, [selectedClient]);

  useEffect(() => {
    dispatch(claimsInvoiceActions.updateState({ key: 'invoicePreview', data: null }));
  }, [selectedPayee, discount, dueInDays]);

  useEffect(() => {
    if (!claimInvoiceHistoryList) return;
    const transformed = filterAndTransformInvoiceTableData(claimInvoiceHistoryList);
    setTransformedTableData(transformed);
  }, [claimInvoiceHistoryList]);

  const previewTabDisabled = !invoicePreview;

  const selectedClaims = transformedTableData.filter((it) => it.selected);
  const totalNumClaimSelected = selectedClaims.length;
  const totalDue = selectedClaims.reduce((sum, it) => sum + it.patient_responsibility, 0) ?? 0;

  return (
    <PageContainer sx={{ py: 3 }}>
      {(invoiceDataLoading || claimDataLoading || clientsDataLoading) && <FProgressBar />}
      <Tabs
        value={tab}
        onChange={(_: SyntheticEvent, newValue: number) => {
          setTab(newValue);
        }}
        aria-label="basic tabs example"
        sx={{
          minHeight: 32,
          '& .MuiTab-root': {
            padding: '0 0.5rem',
            textTransform: 'none',
            fontWeight: 400,
            minHeight: 32,
            color: FORTA_BLUE.primary,
          },
        }}
      >
        <Tab
          label="Build invoice"
          sx={{
            marginRight: 2,
          }}
          {...a11yProps(0)}
          data-testid="buildInvoiceTab"
        />
        <Tab
          label="Invoice preview"
          {...a11yProps(1)}
          data-testid="invoicePreviewTab"
          disabled={previewTabDisabled}
        />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <InvoiceBuilder
          totalDue={totalDue}
          totalNumClaimSelected={totalNumClaimSelected}
          onTabChange={setTab}
          selectedClient={selectedClient}
          selectedPayee={selectedPayee}
          isSelectedAll={isSelectedAll}
          dueInDays={dueInDays}
          transformedTableData={transformedTableData}
          discount={discount}
          setDiscount={setDiscount}
          setTransformedTableData={setTransformedTableData}
          setSelectedClient={setSelectedClient}
          setSelectedPayee={setSelectedPayee}
          setIsSelectedAll={setIsSelectedAll}
        />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <InvoicePreview previewMode invoiceData={invoicePreview} selectedPayee={selectedPayee} />
      </TabPanel>
    </PageContainer>
  );
}

export default BuildInvoice;
